import i18n from "i18next";
import {
	faBan,
	faBars,
	faCheck,
	faPen,
	faPlay,
	faSave,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { TaskActionsApi } from "src/api/generated/tasks/api/taskActionsApi.ts";
import { AsyncButton } from "../../../common/buttons/AsyncButton.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { AddDocumentsToObjectButton } from "src/components/views/documents/AddDocumentsToObjectButton.tsx";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { DocumentsOfObjectSourceButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectSourceButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { TasksViewProps } from "src/components/views/tasks/TasksView.tsx";
import { Box } from "@mui/material";
import { Task } from "src/api/generated/postgres/db/types/tasks/tables/task.ts";
import { taskIsSaved } from "src/components/views/tasks/taskUtils.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { AavoIconButton } from "../../../common/buttons/AavoIconButton.tsx";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { CustomUiActionObjectMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";

export interface SingleTaskMobileViewFooterProps
	extends Pick<TasksViewProps, "onTaskCompleted" | "onTaskCompletionReverted"> {
	task: Task;
	isEditing: boolean;
	isNewTask: boolean;
	onSave: () => Promise<void>;
	onCancelEdit: () => void;
	onStartEdit: () => void;
	refreshTask: () => Promise<unknown>;
}

export const SingleTaskMobileViewFooter = ({
	task,
	isEditing,
	isNewTask,
	onSave,
	onCancelEdit,
	onStartEdit,
	refreshTask,
	onTaskCompleted,
	onTaskCompletionReverted,
}: SingleTaskMobileViewFooterProps) => {
	const [openContextMenu, contextMenuState] = useContextMenu();

	if (isEditing) {
		return (
			<HorizontalBox
				sx={{
					paddingY: 1,
					paddingX: 2,
					gap: 1,
					"& .MuiButton-root": {
						fontSize: "1rem",
					},
				}}
			>
				{!isNewTask && (
					<AsyncButton
						label={i18n.t("cancel")}
						variant={"outlined"}
						onClick={onCancelEdit}
					/>
				)}
				<Box sx={{ flex: 1 }} />
				<AsyncButton
					label={i18n.t("save")}
					icon={faSave}
					variant={"contained"}
					sx={isNewTask ? { flex: 1 } : undefined}
					onClick={onSave}
				/>
			</HorizontalBox>
		);
	}

	const primaryAction = getPrimaryAction();

	return (
		<VerticalBox
			sx={{
				paddingY: 1,
				paddingRight: 2,
				paddingLeft: 1,
				gap: 1,
				"& .MuiButton-root": {
					fontSize: "1rem",
				},
			}}
		>
			{taskIsSaved(task) && (
				<HorizontalBox
					sx={{
						gap: 0.5,
						alignItems: "center",
					}}
				>
					<AavoIconButton
						icon={faBars}
						color={"black"}
						onClick={(e) => {
							openContextMenu({
								mouseEvent: e,
								content: <TaskMenuContent task={task} refreshTask={refreshTask} />,
							});
						}}
					/>
					<Box sx={{ flex: 1 }} />
					<DocumentsOfObjectSourceButton
						sourceRef={nullableAavoObjectRef(task.sourceType, task.sourceId)}
					/>
					<DocumentsOfObjectButton
						objectRef={{ objectType: "TASK", objectId: task.taskId }}
					/>
					<AddDocumentsToObjectButton
						objectRef={{ objectType: "TASK", objectId: task.taskId }}
					/>
				</HorizontalBox>
			)}
			<HorizontalBox
				sx={{
					justifyContent: "flex-end",
					gap: 1,
				}}
			>
				<AsyncButton
					label={i18n.t("edit")}
					icon={faPen}
					variant={"outlined"}
					onClick={onStartEdit}
				/>
				{primaryAction != null && primaryAction}
			</HorizontalBox>
			<AavoContextMenu state={contextMenuState} />
		</VerticalBox>
	);

	function getPrimaryAction() {
		if (task.taskState === "RELEASED") {
			return (
				<AsyncButton
					label={i18n.t("start")}
					icon={faPlay}
					variant={"contained"}
					sx={{ flex: 1 }}
					onClick={async () => {
						await TaskActionsApi.startTask({ taskId: task.taskId });
						await refreshTask();
					}}
				/>
			);
		}
		if (task.taskState === "STARTED") {
			return (
				<AsyncButton
					label={i18n.t("complete")}
					icon={faCheck}
					variant={"contained"}
					sx={{ flex: 1 }}
					onClick={async () => {
						await TaskActionsApi.completeTask({ taskId: task.taskId });
						await refreshTask();
						onTaskCompleted?.(task);
					}}
				/>
			);
		}
		if (task.taskState === "COMPLETED") {
			return (
				<AsyncButton
					label={i18n.t("revert_complete")}
					icon={faUndo}
					variant={"contained"}
					color={"secondary"}
					sx={{ flex: 1 }}
					onClick={async () => {
						await TaskActionsApi.revertCompleteTask({ taskId: task.taskId });
						await refreshTask();
						onTaskCompletionReverted?.(task);
					}}
				/>
			);
		}
		return null;
	}
};

interface TaskMenuContentProps {
	task: Task;
	refreshTask: () => Promise<unknown>;
}

const TaskMenuContent = ({ task, refreshTask }: TaskMenuContentProps) => {
	const showConfirmDialog = useConfirmDialog();
	return [
		<CustomUiActionObjectMenuItems
			key={"customFunctionUiActions"}
			view={"TASKS"}
			objectIds={[task.taskId]}
		/>,
		task.taskState === "STARTED" && (
			<AsyncMenuButton
				key={"revertStart"}
				label={i18n.t("revert_start")}
				icon={faUndo}
				onClick={async () => {
					await TaskActionsApi.revertStartTask({ taskId: task.taskId });
					await refreshTask();
				}}
			/>
		),
		task.taskState !== "CANCELLED" && (
			<AsyncMenuButton
				key={"cancelTask"}
				label={i18n.t("cancel_task")}
				icon={faBan}
				color={"primary"}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						message: i18n.t("confirm_cancel_task"),
						cancelButtonText: i18n.t("no"),
					});
					if (!confirmed) return;
					await TaskActionsApi.cancelTasks({ taskIds: [task.taskId] });
					await refreshTask();
				}}
			/>
		),
	];
};
